import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft } from '@/fontawesome';

interface PageHeaderProps {
  title: string;
  icon?: IconDefinition;
  description?: string;
  /** Right-aligned slot for the page's primary actions. */
  actions?: ReactNode;
  /** Renders a back arrow linking to this route. */
  backTo?: string;
  backLabel?: string;
  className?: string;
}

export default function PageHeader({
  title,
  icon,
  description,
  actions,
  backTo,
  backLabel = 'Zurück',
  className = '',
}: PageHeaderProps) {
  return (
    <div className={`flex flex-wrap items-start justify-between gap-3 ${className}`}>
      <div className="flex items-start gap-3 min-w-0">
        {backTo !== undefined && (
          <Link
            to={backTo}
            aria-label={backLabel}
            title={backLabel}
            className="mt-1.5 p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer shrink-0"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
        )}
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            {icon !== undefined && (
              <FontAwesomeIcon icon={icon} className="w-7 h-7 text-blue-600 dark:text-blue-400 shrink-0" />
            )}
            <span className="min-w-0 break-words">{title}</span>
          </h1>
          {description !== undefined && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
      </div>
      {actions !== undefined && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
